// /app/api/auth/[...nextauth]/route.js
import NextAuth from 'next-auth'
import CredentialsProvider from 'next-auth/providers/credentials'
import { getRevalidatDefault_POST_NoCache } from '@lib'

const isReal = () => process.env.USE_REAL_API_Login === 'true'
const msFromNow = (s) => Date.now() + Number(s || 3600) * 1000
const genFake = (pfx) =>
  `${pfx}.${Math.random().toString(36).slice(2)}.${Date.now()}`

const maskLoginValue = (value) => {
  const text = String(value || '').trim()
  if (!text) return ''

  const atIndex = text.indexOf('@')
  if (atIndex > 0) {
    const local = text.slice(0, atIndex)
    const domain = text.slice(atIndex)
    const maskedLocal = local.length <= 2 ? '*'.repeat(local.length) : `${local.slice(0, 2)}***`
    return `${maskedLocal}${domain}`
  }

  if (text.length <= 4) return '*'.repeat(text.length)
  return `${text.slice(0, 2)}***${text.slice(-2)}`
}

const shortLogValue = (value, maxLength = 120) => {
  const text = String(value || '').trim()
  return text.length <= maxLength ? text : `${text.slice(0, maxLength)}...`
}

const getStoreNumberRaw = () =>
  process.env.STORE_NUMBER ||
  process.env.NEXT_PUBLIC_STORE_NUMBER ||
  process.env.NEXT_PUBLIC_STORENUMBER ||
  ''

const toStoreNumber = () => {
  const raw = getStoreNumberRaw()
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : raw // 優先送 number，失敗才送原字串
}

export const authOptions = {
  trustHost: true,
  session: { strategy: 'jwt', maxAge: 30 * 24 * 60 * 60 },

  providers: [
    CredentialsProvider({
      name: 'Credentials',
      credentials: {
        account: { label: '帳號', type: 'text' },
        password: { label: '密碼', type: 'password' },
        rememberMe: { label: '記住我', type: 'checkbox' },
        deviceInfo: { label: '裝置資訊', type: 'text' },
        lineId: { label: 'LineID', type: 'text' },
      },

      async authorize(credentials) {
        const storeNumber = toStoreNumber()

        const account = (credentials?.account ?? '').toString().trim()
        const password = (credentials?.password ?? '').toString() // 密碼通常不 trim
        const rememberMe =
          credentials?.rememberMe === 'true' || credentials?.rememberMe === true

        const lineId = (credentials?.lineId ?? credentials?.LineID ?? '')
          .toString()
          .trim()

        const deviceInfo = (credentials?.deviceInfo ?? '').toString()

        const isLineOnlyLogin = !!lineId && !account && !password
        const isNormalLogin = !!account && !!password

        if (!isLineOnlyLogin && !isNormalLogin) {
          console.warn(
            '[NextAuth] Missing credentials: need (account+password) OR (lineId only).'
          )
          return null
        }

        // ✅ 依模式組 payload
        const payload = {
          StoreNumber: storeNumber,
          RememberMe: rememberMe,
          DeviceInfo: deviceInfo,
          ...(isLineOnlyLogin
            ? { LineID: lineId }
            : {
                Account: account,
                Password: password,
                ...(lineId ? { LineID: lineId } : {}),
              }),
        }

        if (!isReal()) {
          return {
            MemberID: 1,
            UserName: account || lineId || 'demo.user',
            LastName: '王',
            Gender: 1,
            Mail01: 'demo@example.com',
            Mobile: '0912345678',
            Birthday: '1990-01-01',
            BonusPoints: 150,
            OfflineMemberID: '0000000001',
            AccessToken: genFake('atk'),
            RefreshToken: genFake('rtk'),
            TokenType: 'Bearer',
            ExpiresIn: 3600,
            CardClass: '01',
          }
        }

        const url = `${process.env.API_URL}/api/front/v1/member/login`

        const loginLogPayload = {
          StoreNumber: storeNumber,
          Mode: isLineOnlyLogin ? 'LINE_ONLY' : 'NORMAL',
          Account: maskLoginValue(account),
          LineID: maskLoginValue(lineId),
          RememberMe: rememberMe,
          DeviceInfo: shortLogValue(deviceInfo),
        }

        console.log('[NextAuth] login attempt =', loginLogPayload)

        try {
          const res = await fetch(url, {
            ...getRevalidatDefault_POST_NoCache(),
            body: JSON.stringify(payload),
          })

          const rawText = await res.text()
          let json = {}
          try {
            json = rawText ? JSON.parse(rawText) : {}
          } catch {
            json = {}
          }

          if (res.ok && !json?.Error && json?.Data?.MemberID) return json.Data

          console.warn('[NextAuth] Login failed:', {
            ...loginLogPayload,
            status: res.status,
            statusText: res.statusText,
            Error: json?.Error,
            Data: json?.Data,
            rawText: shortLogValue(rawText, 500),
          })
          return null
        } catch (error) {
          console.error('[NextAuth] Login exception:', {
            ...loginLogPayload,
            message: error?.message || String(error),
            stack: error?.stack,
          })
          return null
        }
      },
    }),
  ],

  callbacks: {
    async jwt({ token, user }) {
      if (user) {
        token.MemberID = user.MemberID
        token.UserName = user.UserName
        token.LastName = user.LastName
        token.Gender = user.Gender
        token.Mail01 = user.Mail01
        token.Mobile = user.Mobile
        token.Birthday = user.Birthday
        token.BonusPoints = user.BonusPoints
        token.OfflineMemberID = user.OfflineMemberID
        token.CardClass = user.CardClass

        token.AccessToken = user.AccessToken
        token.RefreshToken = user.RefreshToken
        token.TokenType = user.TokenType || 'Bearer'
        token.AccessTokenExpires = msFromNow(user.ExpiresIn || 3600)
        return token
      }

      const willExpire =
        !token.AccessTokenExpires ||
        Date.now() > Number(token.AccessTokenExpires) - 15_000
      if (!willExpire) return token

      if (!isReal()) {
        token.AccessToken = genFake('atk')
        token.RefreshToken = genFake('rtk')
        token.AccessTokenExpires = msFromNow(3600)
        return token
      }

      const url = `${process.env.API_URL}/api/front/v1/member/refresh_Token`
      const res = await fetch(url, {
        ...getRevalidatDefault_POST_NoCache(),
        body: JSON.stringify({ RefreshToken: token.RefreshToken }),
      })
      const json = await res.json().catch(() => ({}))
      if (res.ok && !json?.Error && json?.Data?.AccessToken) {
        token.AccessToken = json.Data.AccessToken
        token.RefreshToken = json.Data.RefreshToken || token.RefreshToken
        token.TokenType = json.Data.TokenType || token.TokenType
        token.AccessTokenExpires = msFromNow(json.Data.ExpiresIn || 3600)
        return token
      }
      console.warn('Refresh token failed:', json?.Error || json)
      token.error = 'RefreshTokenError'
      return token
    },

    async session({ session, token }) {
      session.error = token.error || null
      session.AccessToken = token.AccessToken
      session.TokenType = token.TokenType || 'Bearer'
      session.AccessTokenExpires = token.AccessTokenExpires
      session.RefreshToken = token.RefreshToken

      session.MemberID = token.MemberID
      session.OfflineMemberID = token.OfflineMemberID
      session.CardClass = token.CardClass

      session.user = {
        MemberID: token.MemberID,
        UserName: token.UserName,
        LastName: token.LastName,
        Gender: token.Gender,
        Mail01: token.Mail01,
        Mobile: token.Mobile,
        Birthday: token.Birthday,
        BonusPoints: token.BonusPoints,
        OfflineMemberID: token.OfflineMemberID,
        CardClass: token.CardClass,
      }
      return session
    },
  },

  pages: { signIn: '/login' },
  secret: process.env.NEXTAUTH_SECRET,
}

const handler = NextAuth(authOptions)
export { handler as GET, handler as POST }
